import React, { useEffect, useState } from "react";
import { Link2, Search, Trash2 } from "lucide-react";
import {
  createCatalogRelation,
  deleteCatalogRelation,
  fetchRelatedItems,
  fetchRelationCategories,
  searchCatalogRelations,
  updateCatalogRelation,
} from "../../api/CatalogAPIs";
import { displayAr, resolveMediaUrl } from "./catalogUi";

const emptyGroups = { categories: [], categoryTypes: [] };

const RelatedItemsPanel = ({ ownerKind, ownerId, ownerVariants = [], compact = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [targetKind, setTargetKind] = useState("product");
  const [mode, setMode] = useState("search");
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState(emptyGroups);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [relatedVariantId, setRelatedVariantId] = useState("");
  const [ownerVariantId, setOwnerVariantId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchRelatedItems(ownerKind, ownerId);
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err.message || "تعذر تحميل الروابط");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerKind, ownerId, reloadKey]);

  useEffect(() => {
    setResults([]);
    setTotalCount(0);
    setPage(1);
    setSelected(null);
    setRelatedVariantId("");
    setOwnerVariantId("");
    setSelectedTypeId(null);
    setSelectedCategoryId(null);
    setGroups(emptyGroups);
  }, [targetKind, mode]);

  useEffect(() => {
    if (mode !== "browse") return undefined;
    let cancelled = false;
    fetchRelationCategories(targetKind)
      .then((data) => {
        if (!cancelled) setGroups(data || emptyGroups);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "تعذر تحميل الفئات");
      });
    return () => {
      cancelled = true;
    };
  }, [mode, targetKind]);

  const runSearch = async (nextPage, category, typeId) => {
    const pageNumber = nextPage || 1;
    const categoryId = category === undefined ? selectedCategoryId : category;
    const categoryTypeId = typeId === undefined ? selectedTypeId : typeId;
    setSearching(true);
    setError("");
    try {
      const params = {
        kind: targetKind,
        page: pageNumber,
        pageSize: 10,
        excludeId: targetKind === ownerKind ? ownerId : undefined,
      };
      if (mode === "search") {
        const term = query.trim();
        if (!term) {
          setError("اكتب رقم أو كود أو اسم.");
          setSearching(false);
          return;
        }
        params.query = term;
      } else if (targetKind === "design") {
        if (!categoryId) {
          setError("اختار فئة الأول.");
          setSearching(false);
          return;
        }
        params.categoryId = categoryId;
      } else if (!categoryTypeId && !categoryId) {
        setError("اختار نوع فئة الأول.");
        setSearching(false);
        return;
      } else {
        if (categoryTypeId) params.categoryTypeId = categoryTypeId;
        if (categoryId) params.categoryId = categoryId;
      }

      const data = await searchCatalogRelations(params);
      setResults(data?.items || []);
      setTotalCount(data?.totalCount || 0);
      setPage(data?.page || pageNumber);
    } catch (err) {
      setError(err.message || "تعذر البحث");
    } finally {
      setSearching(false);
    }
  };

  const choose = (item) => {
    setSelected(item);
    setRelatedVariantId(item.matchedVariantId ? String(item.matchedVariantId) : "");
    setOwnerVariantId("");
    setMessage("");
  };

  const addRelation = async () => {
    if (!selected) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await createCatalogRelation({
        ownerKind,
        ownerId: Number(ownerId),
        relatedKind: selected.kind,
        relatedId: selected.id,
        relatedVariantId: relatedVariantId ? Number(relatedVariantId) : null,
        ownerVariantId: ownerVariantId ? Number(ownerVariantId) : null,
      });
      setMessage("تم الربط من الطرفين.");
      setSelected(null);
      setRelatedVariantId("");
      setOwnerVariantId("");
      setReloadKey((value) => value + 1);
    } catch (err) {
      setError(err.message || "تعذر الربط");
    } finally {
      setSaving(false);
    }
  };

  const changeVariants = async (item, relatedId, ownerIdValue) => {
    setError("");
    try {
      await updateCatalogRelation(item.relationId, {
        relatedVariantId: relatedId ? Number(relatedId) : null,
        ownerVariantId: ownerIdValue ? Number(ownerIdValue) : null,
      });
      setReloadKey((value) => value + 1);
    } catch (err) {
      setError(err.message || "تعذر تحديث اللون");
    }
  };

  const removeRelation = async (item) => {
    if (!window.confirm("إزالة الربط؟ هيتمسح من الصفحتين.")) return;
    setError("");
    try {
      await deleteCatalogRelation(item.relationId);
      setReloadKey((value) => value + 1);
    } catch (err) {
      setError(err.message || "تعذر إزالة الربط");
    }
  };

  const browseGroups = targetKind === "design" ? groups.categories || [] : groups.categoryTypes || [];
  const subcategories = (groups.categories || []).filter(
    (category) => String(category.categoryTypeId) === String(selectedTypeId)
  );
  const pages = Math.max(1, Math.ceil(totalCount / 10));

  return (
    <section
      dir="rtl"
      className={
        compact
          ? "shrink-0 max-h-[42vh] overflow-y-auto border-t bg-gray-50 p-4"
          : "mt-8 bg-white/90 rounded-2xl shadow border border-white/30 p-5 md:p-8"
      }
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Link2 size={18} />
        <h2 className="text-xl font-bold">اشتري أيضاً معه</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        الربط متبادل. لو ربطت ديزاين بمنتج، هيظهروا عند بعض في صفحة كل واحد.
      </p>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {message && <p className="mb-3 text-sm text-green-700">{message}</p>}

      {loading ? (
        <p className="text-gray-500">جاري تحميل الروابط...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 mb-4">لسه مفيش ربط.</p>
      ) : (
        <div className="space-y-3 mb-5">
          {items.map((item) => (
            <div key={item.relationId} className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 md:flex-row md:items-center">
              {item.imageUrl ? (
                <img src={resolveMediaUrl(item.imageUrl)} alt="" className="w-14 h-14 rounded-lg object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-gray-100" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                    {item.kind === "design" ? "ديزاين" : "منتج"}
                  </span>
                  <span className="font-semibold truncate">{displayAr(item.name)}</span>
                  <span className="text-xs text-gray-500">#{item.id}</span>
                  {item.code && <span className="text-xs font-mono text-gray-500">{item.code}</span>}
                </div>
                {item.variantLabel && <p className="text-sm text-gray-600 mt-1">{item.variantLabel}</p>}
              </div>
              <div className="flex flex-col gap-2 md:w-64">
                {item.kind === "product" && (item.variants || []).length > 0 && (
                  <select
                    value={item.variantId || ""}
                    onChange={(event) => changeVariants(item, event.target.value, item.ownerVariantId)}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-sm"
                  >
                    <option value="">لون المنتج المرتبط: الافتراضي</option>
                    {item.variants.map((variant) => (
                      <option key={variant.productVariantId} value={variant.productVariantId}>
                        {variant.label || variant.code}
                      </option>
                    ))}
                  </select>
                )}
                {ownerKind === "product" && ownerVariants.length > 0 && (
                  <select
                    value={item.ownerVariantId || ""}
                    onChange={(event) => changeVariants(item, item.variantId, event.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-sm"
                  >
                    <option value="">لون المنتج الحالي هناك: الافتراضي</option>
                    {ownerVariants.map((variant) => (
                      <option key={variant.productVariantId} value={variant.productVariantId}>
                        {variantLabel(variant)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeRelation(item)}
                className="p-2 rounded-lg bg-red-50 text-red-700 self-start"
                title="إزالة الربط"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-dashed border-indigo-200 p-3 bg-white">
        <div className="flex flex-wrap gap-2 mb-3">
          <KindButton active={targetKind === "product"} onClick={() => setTargetKind("product")}>
            منتج
          </KindButton>
          <KindButton active={targetKind === "design"} onClick={() => setTargetKind("design")}>
            ديزاين
          </KindButton>
          <KindButton active={mode === "search"} onClick={() => setMode("search")}>
            بحث بالرقم أو الكود
          </KindButton>
          <KindButton active={mode === "browse"} onClick={() => setMode("browse")}>
            تصفح الفئات
          </KindButton>
        </div>

        {mode === "search" ? (
          <div className="flex gap-2 mb-3">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.stopPropagation();
                  runSearch(1);
                }
              }}
              placeholder={targetKind === "design" ? "رقم الديزاين أو الكود أو الاسم" : "رقم المنتج أو كود المتغير أو الاسم"}
              className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
            />
            <button
              type="button"
              onClick={() => runSearch(1)}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold flex items-center gap-2"
            >
              <Search size={16} /> بحث
            </button>
          </div>
        ) : (
          <div className="mb-3 space-y-2">
            <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
              {browseGroups.length === 0 ? (
                <p className="text-sm text-gray-500">مفيش فئات.</p>
              ) : (
                browseGroups.map((group) => {
                  const active =
                    targetKind === "design"
                      ? String(selectedCategoryId) === String(group.categoryId)
                      : String(selectedTypeId) === String(group.categoryTypeId) && !selectedCategoryId;
                  return (
                    <button
                      key={`${group.categoryTypeId || "t"}-${group.categoryId || "c"}-${group.name}`}
                      type="button"
                      onClick={() => {
                        if (targetKind === "design") {
                          setSelectedCategoryId(group.categoryId);
                          setPage(1);
                          runSearch(1, group.categoryId, null);
                        } else {
                          setSelectedTypeId(group.categoryTypeId);
                          setSelectedCategoryId(null);
                          setPage(1);
                          runSearch(1, null, group.categoryTypeId);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm border ${
                        active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-200"
                      }`}
                    >
                      {displayAr(group.name)} ({group.itemCount})
                    </button>
                  );
                })
              )}
            </div>
            {targetKind === "product" && selectedTypeId && subcategories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategoryId(null);
                    runSearch(1, null, selectedTypeId);
                  }}
                  className={`px-3 py-1 rounded-full text-xs border ${
                    !selectedCategoryId ? "bg-gray-800 text-white border-gray-800" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  كل الفئات
                </button>
                {subcategories.map((category) => (
                  <button
                    key={category.categoryId}
                    type="button"
                    onClick={() => {
                      setSelectedCategoryId(category.categoryId);
                      runSearch(1, category.categoryId, selectedTypeId);
                    }}
                    className={`px-3 py-1 rounded-full text-xs border ${
                      String(selectedCategoryId) === String(category.categoryId)
                        ? "bg-gray-800 text-white border-gray-800"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    {displayAr(category.name)} ({category.itemCount})
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {searching ? (
          <p className="text-sm text-gray-500">جاري التحميل...</p>
        ) : (
          <div className="space-y-2">
            {results.map((item) => {
              const active = selected && selected.kind === item.kind && selected.id === item.id;
              return (
                <button
                  key={`${item.kind}-${item.id}`}
                  type="button"
                  onClick={() => choose(item)}
                  className={`w-full text-right flex items-center gap-3 rounded-xl border p-2 ${
                    active ? "border-indigo-500 bg-indigo-50" : "border-gray-200 bg-white"
                  }`}
                >
                  {item.imageUrl ? (
                    <img src={resolveMediaUrl(item.imageUrl)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-100" />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate">{displayAr(item.name)}</span>
                    <span className="block text-xs text-gray-500">
                      #{item.id}
                      {item.code ? ` · ${item.code}` : ""}
                      {item.categoryName ? ` · ${displayAr(item.categoryName)}` : ""}
                      {item.matchedVariantLabel ? ` · ${item.matchedVariantLabel}` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
            {results.length === 0 && (mode === "search" ? query : selectedCategoryId || selectedTypeId) && !searching && (
              <p className="text-sm text-gray-500">مفيش نتائج.</p>
            )}
          </div>
        )}

        {totalCount > 10 && (
          <div className="flex items-center justify-between mt-3 text-sm">
            <button
              type="button"
              disabled={page <= 1 || searching}
              onClick={() => runSearch(page - 1)}
              className="px-3 py-1 rounded-lg border disabled:opacity-40"
            >
              السابق
            </button>
            <span>
              {page} / {pages} — {totalCount} عنصر
            </span>
            <button
              type="button"
              disabled={page >= pages || searching}
              onClick={() => runSearch(page + 1)}
              className="px-3 py-1 rounded-lg border disabled:opacity-40"
            >
              التالي
            </button>
          </div>
        )}

        {selected && (
          <div className="mt-4 space-y-3 border-t pt-3">
            <p className="font-semibold">
              ربط مع {selected.kind === "design" ? "الديزاين" : "المنتج"}: {displayAr(selected.name)} #{selected.id}
            </p>
            {selected.kind === "product" && (selected.variants || []).length > 0 && (
              <label className="block text-sm text-gray-600">
                اللون اللي يظهر للمنتج ده
                <select
                  value={relatedVariantId}
                  onChange={(event) => setRelatedVariantId(event.target.value)}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
                >
                  <option value="">الافتراضي</option>
                  {selected.variants.map((variant) => (
                    <option key={variant.productVariantId} value={variant.productVariantId}>
                      {variant.label || variant.code}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {ownerKind === "product" && ownerVariants.length > 0 && (
              <label className="block text-sm text-gray-600">
                لون المنتج الحالي لما يظهر في الصفحة التانية
                <select
                  value={ownerVariantId}
                  onChange={(event) => setOwnerVariantId(event.target.value)}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
                >
                  <option value="">الافتراضي</option>
                  {ownerVariants.map((variant) => (
                    <option key={variant.productVariantId} value={variant.productVariantId}>
                      {variantLabel(variant)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              onClick={addRelation}
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold disabled:opacity-60"
            >
              {saving ? "جاري الربط..." : "إضافة الربط"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const KindButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
      active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-200"
    }`}
  >
    {children}
  </button>
);

function variantLabel(variant) {
  const metaName = displayAr(variant.nameAr || variant.colorName);
  const typeName = variant.typeName ? displayAr(variant.typeName) : "";
  return [metaName, typeName, variant.code].filter((part) => part && part !== "—").join(" · ");
}

export default RelatedItemsPanel;
